import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useTheme } from './ThemeProvider'
import {
  accentContrast,
  readAccentPreference,
  writeAccentPreference,
  type AccentPreference,
} from './accentPreference'
import { CHART_PALETTE_VAR_NAMES, chartPaletteToCssVars, deriveChartPalette } from './chartPalette'

interface AccentContextValue {
  /** The user's stored choice — a `#rrggbb` hex, or `null` for "Default" (sponsor graphite). */
  accent: AccentPreference
  setAccent: (next: AccentPreference) => void
}

const AccentContext = createContext<AccentContextValue | null>(null)

/**
 * Accent controller (IB-258). Layers a per-user accent colour on top of the
 * existing light/dark/system theme (`ThemeProvider`) — must sit **inside**
 * it (design doc §5), because re-deriving `--iw-accent-contrast` on a theme
 * switch is part of the contract (AC2: "live, ... in both light and dark").
 *
 * Mechanism (design doc §3 — implement exactly this): only two inline
 * custom properties plus one attribute on `<html>`.
 *   - Default (`accent === null`): no `data-accent`, no inline vars — the
 *     graphite defaults in `tokens.css` apply completely untouched.
 *   - A preset or custom hex chosen: `data-accent="custom"` +
 *     `--iw-accent: <hex>` + `--iw-accent-contrast: <computed>`. `tokens.css`
 *     derives `--iw-accent-hover`/`--iw-accent-subtle`/`--iw-focus-ring` from
 *     `--iw-accent` via `color-mix()`, scoped to `[data-accent="custom"]` so
 *     the graphite default block is never touched by this provider.
 *
 * CSS custom properties do the rest, same as `ThemeProvider` — no consuming
 * component re-renders on an accent change.
 *
 * IB-291 extends this SAME effect (no new mechanism) to also inject/remove
 * the 13 theme-derived chart custom properties (`--iw-chart-1…4` +
 * `--iw-chart-seq-1…6` + `--iw-chart-div-neg/-mid/-pos`) computed by
 * `deriveChartPalette` — set alongside `--iw-accent*` for a custom accent,
 * removed alongside it for "Default". Re-running on an `effectiveTheme`
 * change (the dependency this effect already carries) is exactly what a
 * theme-aware chart derivation needs — see `chartPalette.ts`'s header.
 */
export function AccentProvider({ children }: { children: ReactNode }) {
  const { effectiveTheme } = useTheme()
  const [accent, setAccentState] = useState<AccentPreference>(() => readAccentPreference())

  useEffect(() => {
    const root = document.documentElement
    if (accent === null) {
      root.removeAttribute('data-accent')
      root.style.removeProperty('--iw-accent')
      root.style.removeProperty('--iw-accent-contrast')
      for (const varName of CHART_PALETTE_VAR_NAMES) {
        root.style.removeProperty(varName)
      }
      return
    }
    root.setAttribute('data-accent', 'custom')
    root.style.setProperty('--iw-accent', accent)
    root.style.setProperty('--iw-accent-contrast', accentContrast(accent))
    // IB-291: the chart family DOES need re-derivation on a theme switch
    // (the sequential/diverging lightness stops flip direction per theme —
    // chartPalette.ts §2.2/§2.3) — this is the reason `effectiveTheme` is a
    // real dependency of this effect, not just a defensive one.
    const chartVars = chartPaletteToCssVars(deriveChartPalette(accent, effectiveTheme))
    for (const [varName, value] of Object.entries(chartVars)) {
      root.style.setProperty(varName, value)
    }
  }, [accent, effectiveTheme])

  const setAccent = useCallback((next: AccentPreference) => {
    setAccentState(next)
    writeAccentPreference(next)
  }, [])

  const value = useMemo<AccentContextValue>(() => ({ accent, setAccent }), [accent, setAccent])

  return <AccentContext.Provider value={value}>{children}</AccentContext.Provider>
}

/** Throws outside an `AccentProvider` — a missing provider is a wiring bug, not a state to degrade into. */
export function useAccent(): AccentContextValue {
  const ctx = useContext(AccentContext)
  if (!ctx) {
    throw new Error('useAccent() must be called within an <AccentProvider>')
  }
  return ctx
}
